'use client'
import Link from "next/link"
import Image from "next/image"
import { Suspense, useState, useEffect } from "react"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { MaxWidthWrapper } from "./max-width-wrapper"
import { useCart } from "@/context/CartContext"
import { useSimplePet } from "@/context/SimplePetContext"
import { usePetNavigation } from "@/hooks/usePetNavigation"
import { appendOwnerToken } from "@/lib/ownerAccessToken"

/**
 * The header reads ?token from useSearchParams to forward owner-mode through
 * Memorabilia + Status. Next.js requires any client component that calls
 * useSearchParams to live inside a <Suspense> boundary, otherwise pages that
 * include the header (cart, payment-success, etc.) bail out of static
 * rendering at build time. We isolate the searchParams read in a tiny inner
 * component wrapped in Suspense so consumers don't have to think about it.
 */
function HeaderImpl({
  ownerToken,
  marketing = false,
}: {
  ownerToken: string | null
  /**
   * Marketing mode: /owners and its children are public pages that must not
   * leak a linked pet. Hides the pet nav (Memorabilia / Status / Cart) and
   * points the logo at the site root instead of the pet's own page.
   */
  marketing?: boolean
}) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const pathname = usePathname()
  const router = useRouter()
  const toggleMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen)
  const closeMenu = () => setIsMobileMenuOpen(false)

  const [isSticky, setIsSticky] = useState(false)
  const { getCartItemCount } = useCart()
  const { currentPet, clearCurrentPet } = useSimplePet()
  // Everything pet-specific in this header hangs off this one flag.
  const showPetNav = !!currentPet && !marketing
  const { navigateToHome } = usePetNavigation()
  // Carry ?petId= so the keepsake catalog can re-link the pet from the URL even
  // when the in-memory pet store is empty (fresh tab / cleared localStorage).
  const memorialHref = appendOwnerToken(
    currentPet ? `/memorial?petId=${currentPet.id}` : "/memorial",
    ownerToken
  )
  const statusHref = currentPet
    ? appendOwnerToken(`/track/${currentPet.id}`, ownerToken)
    : "/track"

  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 0)
    }
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.classList.add("mobile-menu-open")
    } else {
      document.body.classList.remove("mobile-menu-open")
    }
    return () => {
      document.body.classList.remove("mobile-menu-open")
    }
  }, [isMobileMenuOpen])

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-[99] py-[15px] bg-white ${isSticky ? 'sticky sm:fixed !py-[10px]' : ''}`}>
        <div className="container">
          <MaxWidthWrapper>
            <div className="flex justify-between items-center">
              <button
                onClick={marketing ? () => router.push("/") : navigateToHome}
                className="relative cursor-pointer"
              >
                <Image src="/icons/logo-rgb.svg" alt="PetAftercare" className="w-[250px] md:w-[200px] h-[60px] md:h-[50px] sm:h-auto" width={250} height={60} priority />
              </button>
              {/* Burger icon for mobile */}
              <div className={`${showPetNav ? "hidden sm:flex" : "hidden"} absolute top-[24px] right-[20px]`}>
                <button
                  onClick={toggleMenu}
                  className={`text-[#002e65] z-[99] text-3xl menu-button ${isMobileMenuOpen ? 'active' : ''}`}
                >
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                </button>
              </div>
              {/* Desktop nav - only show when pet is linked (never in marketing mode) */}
              {showPetNav && (
                <div className="sm:hidden flex items-center gap-[30px]">
                  <nav className="flex items-center gap-8 md:gap-4">
                    <Link
                      href={memorialHref}
                      className={`text-[#002e65] font-[18px] font-[600] hover:text-[#0055a4] ${
                        pathname === "/memorial" ? "text-[#004da9] border-b-[3px] border-[#004da9]" : ""
                      }`}
                    >
                      Memorabilia
                    </Link>
                    <div className="w-px h-5 bg-[#002e65]"></div>
                    <Link
                      href={statusHref}
                      className={`text-[#002e65] font-[18px] font-[600] hover:text-[#0055a4] ${
                        pathname?.startsWith("/track/") ? "text-[#004da9] border-b-[3px] border-[#004da9]" : ""
                      }`}
                    >
                      Status
                    </Link>
                    <div className="w-px h-5 bg-[#002e65]"></div>
                    {/* Cart Link */}
                    <Link
                      href="/cart"
                      className="relative text-[#002e65] font-[18px] font-[600] hover:text-[#0055a4] flex items-center gap-2"
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <g fill="none" fillRule="evenodd">
                          <path d="M0 0h24v24H0z"/>
                          <path d="M8 22c-.55 0-1.02-.196-1.412-.587A1.926 1.926 0 0 1 6 20c0-.55.196-1.02.588-1.413A1.926 1.926 0 0 1 8 18c.55 0 1.02.196 1.412.587.392.392.588.863.588 1.413s-.196 1.02-.588 1.413A1.926 1.926 0 0 1 8 22zm10 0c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 16 20c0-.55.196-1.02.587-1.413A1.926 1.926 0 0 1 18 18c.55 0 1.02.196 1.413.587.391.392.587.863.587 1.413s-.196 1.02-.587 1.413A1.926 1.926 0 0 1 18 22zM7.15 6l2.4 5h7l2.75-5H7.15zM6.2 4h14.75c.383 0 .675.17.875.513.2.341.208.687.025 1.037l-3.55 6.4c-.183.333-.43.592-.738.775A1.945 1.945 0 0 1 16.55 13H9.1L8 15h12v2H8c-.75 0-1.317-.33-1.7-.988-.383-.658-.4-1.312-.05-1.962L7.6 11.6 4 4H2V2h3.25l.95 2zm3.35 7.037h7-7z" fill="#004DA9" fillRule="nonzero"/>
                        </g>
                      </svg>
                      <span>Cart</span>
                      {getCartItemCount() > 0 && (
                        <span className="absolute -top-2 -right-2 bg-blue-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                          {getCartItemCount()}
                        </span>
                      )}
                    </Link>
                  </nav>
                </div>
              )}
            </div>
            {/* Mobile nav menu */}
            {isMobileMenuOpen && (
              <div className="hidden sm:block absolute w-full left-0 top-0 text-left bg-[#000f22] shadow-md px-[20px] py-[30px]">
                <button
                  onClick={() => {
                    marketing ? router.push("/") : navigateToHome()
                    closeMenu()
                  }}
                  className="relative mb-[45px] block cursor-pointer"
                >
                  <Image src="/icons/logo-rgb-reversed.svg" alt="PetAftercare" className="w-[160px]" width={250} height={60} priority />
                </button>
                {showPetNav && (
                  <div className="flex flex-wrap gap-[30px]">
                    <Link href={memorialHref} onClick={closeMenu} className="w-full block text-white text-[16px] font-semibold hover:text-[#0055a4]">
                      Memorabilia
                    </Link>
                    <Link href={statusHref} onClick={closeMenu} className="w-full block text-white text-[16px] font-semibold hover:text-[#0055a4]">
                      Status
                    </Link>
                    {/* Mobile Cart Link */}
                    <Link href="/cart" onClick={closeMenu} className="w-full block text-white text-[16px] font-semibold hover:text-[#0055a4] flex items-center gap-2">
                      <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <g fill="none" fillRule="evenodd">
                          <path d="M0 0h24v24H0z"/>
                          <path d="M8 22c-.55 0-1.02-.196-1.412-.587A1.926 1.926 0 0 1 6 20c0-.55.196-1.02.588-1.413A1.926 1.926 0 0 1 8 18c.55 0 1.02.196 1.412.587.392.392.588.863.588 1.413s-.196 1.02-.588 1.413A1.926 1.926 0 0 1 8 22zm10 0c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 16 20c0-.55.196-1.02.587-1.413A1.926 1.926 0 0 1 18 18c.55 0 1.02.196 1.413.587.391.392.587.863.587 1.413s-.196 1.02-.587 1.413A1.926 1.926 0 0 1 18 22zM7.15 6l2.4 5h7l2.75-5H7.15zM6.2 4h14.75c.383 0 .675.17.875.513.2.341.208.687.025 1.037l-3.55 6.4c-.183.333-.43.592-.738.775A1.945 1.945 0 0 1 16.55 13H9.1L8 15h12v2H8c-.75 0-1.317-.33-1.7-.988-.383-.658-.4-1.312-.05-1.962L7.6 11.6 4 4H2V2h3.25l.95 2zm3.35 7.037h7-7z" fill="currentColor" fillRule="nonzero"/>
                        </g>
                      </svg>
                      <span>Cart</span>
                      {getCartItemCount() > 0 && (
                        <span className="bg-blue-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                          {getCartItemCount()}
                        </span>
                      )}
                    </Link>
                  </div>
                )}
              </div>
            )}
          </MaxWidthWrapper>
        </div>
      </header>
    </>
  )
}

function HeaderWithSearchParams({ marketing }: { marketing?: boolean }) {
  const searchParams = useSearchParams()
  const ownerToken = searchParams?.get("token") ?? null
  return <HeaderImpl ownerToken={ownerToken} marketing={marketing} />
}

export default function ClientHeader({ marketing }: { marketing?: boolean } = {}) {
  // Fallback renders the header in non-owner mode (no token); when the
  // Suspense resolves on the client we get the token-bearing variant.
  return (
    <Suspense fallback={<HeaderImpl ownerToken={null} marketing={marketing} />}>
      <HeaderWithSearchParams marketing={marketing} />
    </Suspense>
  )
}
